// @ts-nocheck
import { Unplug } from 'lucide-react';
import { STATUS_MAP, toneStyle } from '@/components/vf/statusMap';
import { useTranslation } from 'react-i18next';
import { dialIntent, effectiveLastCall } from '@/services/dialer/dialRules';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { hasMeetingBooked } from '@/utils/callUtils';

// D9 (2026-10-31) : la palette vient de la carte unique (components/vf/statusMap).
// Les sept palettes d'avant donnaient trois couleurs au même RDV selon l'écran ;
// les choix des tickets #72/#80 (RDV vert foncé, Pas de réponse orange) sont
// remplacés par la spec D9, décision utilisateur du 2026-10-31 (DESIGN.md §3).
export const STATUS_META = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([key, entry]) => {
    const style = toneStyle(entry.tone);
    return [key, { bg: style.bg, tx: style.fg, bd: style.bd, icon: entry.icon }];
  }),
);

/**
 * Correction #72 (réparation du 2026-09-01) — outcomes qui gardent leur propre
 * pastille au lieu d'être absorbés par le générique « Pas de réponse ».
 *
 * Le défaut : `STATUS_META` portait bien les entrées demandées par le client
 * (cassette Voicemail, téléphone barré Canceled, triangle iOS Filter,
 * exclamation No number) mais AUCUN chemin ne les servait. La palette avait été
 * enrichie, pas l'aiguillage. Constaté à l'écran : sur la liste hubspot, 28
 * lignes portant 5 `ios_filter`, 5 `no_number`, 5 `voicemail` et 4 `canceled`
 * n'affichaient que deux pastilles — « Pas de réponse » et « Ne pas appeler ».
 *
 * Volontairement absents de cette liste : `busy`, `failed`, `not_reachable`.
 * Le client ne les cite pas, et leurs métas n'ont pas d'icône — un badge coloré
 * sans icône casserait la règle posée par #72 (« la couleur seule ne suffit
 * pas »). Ils restent donc en « Pas de réponse ». Leur donner une icône est une
 * décision produit, pas une réparation.
 */
const OUTCOMES_AVEC_BADGE_PROPRE = new Set([
  // 2026-09-23, client rule: a call that did not reach a human reads
  // "No answer" (unreachable, not picked up, answering machine without a
  // message, cancelled), and only a message actually left gets its own
  // badge. This supersedes the voicemail and canceled badges of #72.
  'left_voicemail',   // "Message vocal": cassette
  'ios_filter',       // #72 — triangle d'alerte orange
  'no_number',        // #72 — exclamation rouge
]);

// ADR-031 — mapping dialIntent → badge. Source unique de vérité pour le
// statut affiché dans le tableau. Plus de divergence entre prospect.status
// brut et la disposition réelle du dernier appel.
//
// Cas spécial 'archived' : la couleur dépend du sous-cas (RDV pris / pas
// intéressé / DNC), donc on résout via le call_outcome OU le status DNC.
export function resolveStatusMetaFromIntent(prospect, intent, lastCall, totalCalls, t) {
  // A status set by hand wins over the last call when newer (dialRules).
  const effectiveCall = effectiveLastCall(prospect, lastCall);
  const outcome = effectiveCall?.call_outcome;

  if (intent === 'archived') {
    // Sous-discriminants : DNC > RDV pris > Pas intéressé
    if (prospect.status === 'do_not_call' || prospect.do_not_call === true) {
      return { key: 'do_not_call', suffix: null, tooltip: t('dialer:statusPill.markedDoNotCall') };
    }
    if (hasMeetingBooked(effectiveCall)) {
      return { key: 'meeting_booked', suffix: null, tooltip: t('dialer:statusPill.bookedClosed') };
    }
    if (outcome === 'not_interested') {
      return { key: 'not_interested', suffix: null, tooltip: t('dialer:statusPill.notInterestedClosed') };
    }
    // #72 (réparation) — « No number → exclamation rouge ». Un prospect dont le
    // numéro n'est pas attribué tombait dans le badge DNC générique : la ligne
    // disait « Ne pas appeler » alors que le problème est le numéro, pas le
    // consentement. Placé APRÈS le test DNC : un DNC reste un DNC d'abord.
    if (prospect.phone_unassigned === true) {
      return { key: 'no_number', suffix: null, tooltip: t('dialer:statusPill.noNumber') };
    }
    return { key: 'do_not_call', suffix: null, tooltip: t('dialer:statusPill.archived') };
  }

  if (intent === 'callback') {
    return { key: 'callback_requested', suffix: null, tooltip: t('dialer:statusPill.callbackRequested') };
  }

  if (intent === 'contacted') {
    return { key: 'contacted', suffix: null, tooltip: t('dialer:statusPill.humanReached') };
  }

  if (intent === 'no_answer') {
    // #72 (réparation) — le bucket `no_answer` de dialIntent absorbe tout ce qui
    // n'a pas joint d'humain : messagerie, appel annulé, filtre iOS, numéro
    // inexistant. C'est juste pour le TRI, faux pour l'AFFICHAGE : le client
    // veut lire lequel des quatre c'était. On sert donc la pastille de l'outcome
    // quand il en a une, et « Pas de réponse » sinon.
    //
    // `dialIntent` n'est pas touché : il pilote le tri, la pile d'appel et le
    // droit de rappeler. Un badge plus précis ne doit rien changer au
    // comportement du dialer. Sortir `ios_filter` de la pile d'appel serait une
    // décision séparée, à poser dans NO_ANSWER_OUTCOMES (dialRules.ts).
    const propre = outcome && OUTCOMES_AVEC_BADGE_PROPRE.has(outcome) ? outcome : null;
    const key = propre || 'no_answer';
    // Le suffixe « · N » est conservé sur TOUTES ces pastilles. Le nombre de
    // tentatives est aussi utile sur « Messagerie · 3 » que sur « Pas de
    // réponse · 3 », et le retirer sur certaines lignes seulement rendrait la
    // colonne incohérente à la lecture.
    return {
      key,
      suffix: totalCalls > 0 ? ` · ${totalCalls}` : null,
      tooltip: propre
        ? t('dialer:statusPill.attemptsNoHumanWithStatus', { status: outcomeLabel(key, t), count: totalCalls })
        : t('dialer:statusPill.attemptsNoHuman', { count: totalCalls }),
    };
  }

  // fresh
  return { key: 'new', suffix: null, tooltip: t('dialer:statusPill.neverCalled') };
}

export function VFStatusPill({ prospect, intent, isBeingCalled, callStatus, connectedAt, lastCall, totalCalls, interrupted = false }) {
  const { t } = useTranslation(['dialer', 'common']);
  // During an active call, show the live Twilio state (dialing / ringing /
  // connected): the intent does not reflect the live state.
  let key = null;
  let suffix = null;
  let tooltip = null;

  if (isBeingCalled && callStatus) {
    if (callStatus === 'connected_to_agent' && !connectedAt) {
      key = 'verifying'; // AMD window, about 3s
    } else if (callStatus in STATUS_META) {
      key = callStatus;
    }
    if (key) tooltip = t('dialer:statusPill.callInProgress');
  } else {
    const resolved = resolveStatusMetaFromIntent(prospect, intent, lastCall, totalCalls, t);
    key = resolved.key;
    suffix = resolved.suffix;
    tooltip = resolved.tooltip;
  }
  // The label is never read from STATUS_META: callOutcomeLabels is the only
  // label source, STATUS_META keeps the colours and icons of this list.
  const meta = key ? STATUS_META[key] : null;

  if (!meta) {
    return (
      <span
        className="inline-flex items-center whitespace-nowrap rounded-vf-sm px-2 h-6 text-[11px] font-semibold"
        style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
      >
        —
      </span>
    );
  }
  return (
    <span
      title={tooltip || undefined}
      // QA-W4-8: « Nouveau » rendered « Nouve au ». The label is one
      // unbreakable run (no tabular figures nor extra tracking on letters);
      // only the count suffix keeps tabular figures.
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-vf-sm px-2 h-6 text-[11px] font-semibold"
      data-testid="status-pill"
      style={{
        background: meta.bg,
        color: meta.tx,
        border: `1px solid ${meta.bd || 'transparent'}`,
      }}
    >
      {/* Correction #72 — une icone par statut : la couleur seule ne suffit
          pas (daltonisme, impression, coup d'oeil rapide sur 40 lignes). */}
      {meta.icon ? <meta.icon className="w-3 h-3 mr-1 shrink-0" aria-hidden="true" /> : null}
      <span className="whitespace-nowrap">{outcomeLabel(key, t)}</span>
      {suffix ? <span className="whitespace-nowrap tabular-nums">{suffix}</span> : null}
      {/* The last line was cut because another prospect answered first: the
          status is unchanged, the icon says why it was not reached. */}
      {interrupted && !isBeingCalled ? (
        <Unplug
          className="w-3 h-3 ml-1 shrink-0"
          role="img"
          aria-label={t('dialer:statusPill.interrupted')}
        >
          <title>{t('dialer:statusPill.interrupted')}</title>
        </Unplug>
      ) : null}
    </span>
  );
}
